import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatRupees, todayISO } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, FileInput, FileOutput, Plus, X as XIcon } from "lucide-react";

const emptyLine = () => ({ description: "", amount: "", category_id: "", sub_category_id: "" });

/**
 * Full-page bill / invoice entry form.
 * kind = "bill"    → Purchase Bill (money owed to vendor)
 * kind = "invoice" → Sales Invoice (money owed by customer)
 */
export default function NewBill({ kind = "bill" }) {
  const nav = useNavigate();
  const isBill = kind === "bill";
  const partyLabel = isBill ? "Vendor" : "Customer";
  const [categories, setCategories] = useState([]);
  const [subs, setSubs] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [parties, setParties] = useState([]);
  const [form, setForm] = useState({
    number: "", date: todayISO(), due_date: "",
    party_name: "", party_phone: "", party_email: "", party_gstin: "",
    description: "", subtotal: "", gst_percent: 18, gst_amount: "", total: "",
    category_id: "", sub_category_id: "", investment_id: "", notes: "",
    lines: [],
  });
  const [busy, setBusy] = useState(false);
  const [docPreview, setDocPreview] = useState(null);

  useEffect(() => {
    // Investments are project-agnostic, load once
    api.get("/investments").then((r) => setInvestments(r.data));
  }, []);

  // Refetch masters whenever the chosen project changes — filters lists by tag.
  useEffect(() => {
    const partyKind = isBill ? "vendor" : "customer";
    const params = form.investment_id ? { investment_id: form.investment_id } : {};
    Promise.all([
      api.get("/categories", { params }),
      api.get("/subcategories", { params }),
      api.get(`/parties`, { params: { ...params, kind: partyKind } }),
    ]).then(([c, s, p]) => {
      setCategories(c.data); setSubs(s.data); setParties(p.data);
    });
  }, [isBill, form.investment_id]);

  // Auto-preview the next document number when project + date change
  useEffect(() => {
    if (!form.investment_id) { setDocPreview(null); return; }
    let cancelled = false;
    api.get(`/bills/next-number`, {
      params: { investment_id: form.investment_id, kind, date: form.date || undefined },
    }).then((r) => { if (!cancelled) setDocPreview(r.data); }).catch(() => { if (!cancelled) setDocPreview(null); });
    return () => { cancelled = true; };
  }, [form.investment_id, form.date, kind]);

  const catsFiltered = categories.filter((c) => isBill ? c.kind !== "income" : c.kind !== "expense");
  const subsFiltered = subs.filter((s) => s.category_id === form.category_id);

  const recompute = (f) => {
    const linesSum = (f.lines || []).reduce((a, l) => a + Number(l.amount || 0), 0);
    const sub = (f.lines || []).length > 0 ? linesSum : Number(f.subtotal || 0);
    const gst = sub * Number(f.gst_percent || 0) / 100;
    return {
      ...f,
      subtotal: (f.lines || []).length > 0 ? sub.toFixed(2) : f.subtotal,
      gst_amount: gst.toFixed(2),
      total: (sub + gst).toFixed(2),
    };
  };

  const addLine = () => setForm((f) => recompute({ ...f, lines: [...(f.lines || []), emptyLine()] }));
  const removeLine = (idx) => setForm((f) => recompute({ ...f, lines: (f.lines || []).filter((_, i) => i !== idx) }));
  const patchLine = (idx, patch) => setForm((f) => {
    const lines = (f.lines || []).map((l, i) => (i === idx ? { ...l, ...patch } : l));
    return recompute({ ...f, lines });
  });

  const onPartyName = (name) => {
    const match = parties.find((p) => p.name.toLowerCase() === name.toLowerCase());
    if (match) setForm((f) => ({
      ...f, party_name: match.name,
      party_gstin: f.party_gstin || match.gstin || "",
      party_phone: f.party_phone || match.phone || "",
      party_email: f.party_email || match.email || "",
    }));
    else setForm((f) => ({ ...f, party_name: name }));
  };

  const save = async () => {
    if (!form.investment_id) return toast.error("Project is required — pick one to generate a document number");
    if (!form.party_name) return toast.error("Party name is required");
    const lines = (form.lines || [])
      .filter((l) => l.description || Number(l.amount || 0) > 0)
      .map((l) => ({
        description: l.description || "",
        amount: Number(l.amount || 0),
        category_id: l.category_id || null,
        sub_category_id: l.sub_category_id || null,
      }));
    if (lines.length === 0 && !form.subtotal) return toast.error("Add at least one line or a subtotal");
    setBusy(true);
    try {
      await api.post("/bills", {
        ...form, kind,
        number: "",  // let server auto-assign running number
        subtotal: Number(form.subtotal || 0),
        gst_percent: Number(form.gst_percent || 0),
        gst_amount: Number(form.gst_amount || 0),
        total: Number(form.total || 0),
        category_id: form.category_id || null,
        sub_category_id: form.sub_category_id || null,
        investment_id: form.investment_id,
        lines,
      });
      // auto-add to Masters if new
      const exists = parties.some((p) => p.name.toLowerCase() === form.party_name.toLowerCase());
      if (!exists && form.party_name) {
        try {
          await api.post("/parties", {
            kind: isBill ? "vendor" : "customer",
            name: form.party_name, phone: form.party_phone || "",
            email: form.party_email || "", gstin: form.party_gstin || "",
          });
        } catch (_) { /* non-fatal */ }
      }
      toast.success(`${isBill ? "Bill" : "Invoice"} saved`);
      nav(isBill ? "/bills" : "/invoices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const Icon = isBill ? FileInput : FileOutput;
  const backTo = isBill ? "/bills" : "/invoices";

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <Link to={backTo} className="inline-flex items-center gap-2 text-[12px] text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to {isBill ? "Purchase Bills" : "Sales Invoices"}
      </Link>

      <div>
        <div className="eyebrow flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-red-600" />
          {isBill ? "PAYABLE" : "RECEIVABLE"}
        </div>
        <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="new-bill-title">
          New <em className="italic">{isBill ? "bill" : "invoice"}.</em>
        </h1>
      </div>

      <div className="border border-slate-200 bg-white rounded-md p-6 space-y-5">
        {/* Project (Investment) selection — ALWAYS first, mandatory */}
        <div className="border-b border-slate-100 pb-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Project <span className="text-red-600">*</span></Label>
              <Select value={form.investment_id || ""} onValueChange={(v) => setForm({ ...form, investment_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="nb-project"><SelectValue placeholder="Pick a project to enable numbering" /></SelectTrigger>
                <SelectContent>
                  {investments.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.name} <span className="text-slate-400">· {i.type}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{isBill ? "Bill" : "Invoice"} number <span className="text-slate-400 text-[10px]">(auto)</span></Label>
              <Input value={docPreview ? docPreview.document_number : ""} readOnly
                placeholder={form.investment_id ? "Generating…" : "Select a project first"}
                className="mt-1.5 bg-slate-50 font-mono-fin text-slate-900 tracking-wider" data-testid="nb-doc-number" />
              {docPreview && (
                <div className="mt-1 text-[10px] text-slate-500">
                  Project <span className="font-mono-fin text-slate-700">{docPreview.project_code}</span> · FY <span className="font-mono-fin">{docPreview.fy}</span> · running #{docPreview.next_seq}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" /></div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{partyLabel} name <span className="text-red-600">*</span></Label>
            <Input value={form.party_name} onChange={(e) => onPartyName(e.target.value)}
              className="mt-1.5" data-testid="nb-party" autoComplete="off"
              list={`nb-parties-${kind}`}
              placeholder={`Type to search ${partyLabel.toLowerCase()}s…`}
            />
            <datalist id={`nb-parties-${kind}`}>
              {parties.map((p) => <option key={p.id} value={p.name}>{p.gstin || p.phone || ""}</option>)}
            </datalist>
          </div>
          <div><Label>GSTIN</Label><Input value={form.party_gstin} onChange={(e) => setForm({ ...form, party_gstin: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Phone</Label><Input value={form.party_phone} onChange={(e) => setForm({ ...form, party_phone: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Email</Label><Input value={form.party_email} onChange={(e) => setForm({ ...form, party_email: e.target.value })} className="mt-1.5" /></div>
        </div>

        <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>

        {/* Line items */}
        <div className="border border-slate-200 rounded-md bg-slate-50/50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
            <div>
              <Label className="text-[12px] font-semibold text-slate-900">Line items</Label>
              <div className="text-[10.5px] text-slate-500 mt-0.5">
                {(form.lines || []).length === 0
                  ? "Add rows to split by category — or leave empty and use subtotal below."
                  : `${(form.lines || []).length} line${(form.lines || []).length > 1 ? "s" : ""} · auto-summed into subtotal below`}
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addLine} className="h-8 gap-1.5 rounded-md text-[12px]" data-testid="nb-add-line">
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
          </div>

          {(form.lines || []).length > 0 && (
            <div className="hidden md:grid grid-cols-[minmax(0,1fr)_180px_180px_160px_36px] gap-3 px-4 py-2 border-b border-slate-200 bg-slate-100/70 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
              <div>Description</div>
              <div>Category</div>
              <div>Sub-category</div>
              <div className="text-right">Amount (Rs)</div>
              <div />
            </div>
          )}

          <div className="divide-y divide-slate-200/80">
            {(form.lines || []).map((line, idx) => {
              const lineSubs = subs.filter((s) => s.category_id === line.category_id);
              return (
                <div key={idx} className="px-4 py-3 bg-white hover:bg-slate-50/60" data-testid={`nb-line-${idx}`}>
                  {/* Desktop: single-row grid; Mobile: stacked */}
                  <div className="md:grid md:grid-cols-[minmax(0,1fr)_180px_180px_160px_36px] md:gap-3 md:items-center space-y-2 md:space-y-0">
                    <div>
                      <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500 mb-1">Description</div>
                      <Input value={line.description}
                        onChange={(e) => patchLine(idx, { description: e.target.value })}
                        placeholder="e.g. Consultancy · Sept"
                        className="h-9 text-[13px]"
                        data-testid={`nb-line-desc-${idx}`} />
                    </div>
                    <div>
                      <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500 mb-1">Category</div>
                      <Select value={line.category_id || "none"}
                        onValueChange={(v) => patchLine(idx, { category_id: v === "none" ? "" : v, sub_category_id: "" })}>
                        <SelectTrigger className="h-9 text-[13px]" data-testid={`nb-line-cat-${idx}`}>
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {catsFiltered.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sub-category</div>
                      <Select value={line.sub_category_id || "none"}
                        onValueChange={(v) => patchLine(idx, { sub_category_id: v === "none" ? "" : v })}
                        disabled={!line.category_id}>
                        <SelectTrigger className="h-9 text-[13px]" data-testid={`nb-line-sub-${idx}`}>
                          <SelectValue placeholder={line.category_id ? "Sub-cat" : "Pick category first"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {lineSubs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="md:hidden text-[10px] uppercase tracking-wider text-slate-500 mb-1">Amount (Rs)</div>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-slate-400 font-mono-fin pointer-events-none">₹</span>
                        <Input type="number" inputMode="decimal" step="0.01"
                          value={line.amount}
                          onChange={(e) => patchLine(idx, { amount: e.target.value })}
                          placeholder="0.00"
                          className="h-9 text-[14px] text-right font-mono-fin pl-6 pr-3 tabular-nums"
                          data-testid={`nb-line-amt-${idx}`} />
                      </div>
                    </div>
                    <div className="flex md:justify-center">
                      <button type="button" onClick={() => removeLine(idx)}
                        className="md:h-9 md:w-9 h-8 px-2 md:px-0 flex items-center justify-center gap-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md border md:border-transparent border-slate-200 text-[11px]"
                        title="Remove line"
                        data-testid={`nb-line-del-${idx}`}>
                        <XIcon className="h-4 w-4" />
                        <span className="md:hidden">Remove line</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {(form.lines || []).length === 0 && (
              <div className="px-4 py-6 text-center text-[12px] text-slate-500 bg-white">
                No lines yet. Click <span className="font-medium text-slate-700">Add line</span> to split by category, or enter a subtotal below.
              </div>
            )}

            {(form.lines || []).length > 0 && (
              <div className="px-4 py-3 bg-slate-100/80 flex items-center justify-between text-[12px]">
                <span className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold">Lines subtotal</span>
                <span className="font-mono-fin text-slate-900 font-semibold text-[14px] tabular-nums">
                  {formatRupees((form.lines || []).reduce((a, l) => a + Number(l.amount || 0), 0))}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label>Subtotal</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-slate-400 font-mono-fin pointer-events-none">₹</span>
              <Input type="number" inputMode="decimal" step="0.01"
                value={form.subtotal}
                onChange={(e) => setForm(recompute({ ...form, subtotal: e.target.value }))}
                className="h-10 font-mono-fin text-right text-[14px] pl-6 pr-3 tabular-nums"
                disabled={(form.lines || []).length > 0}
                data-testid="nb-subtotal" />
            </div>
          </div>
          <div>
            <Label>GST %</Label>
            <Input type="number" inputMode="decimal" step="0.01"
              value={form.gst_percent}
              onChange={(e) => setForm(recompute({ ...form, gst_percent: e.target.value }))}
              className="h-10 mt-1.5 text-right font-mono-fin tabular-nums"
              data-testid="nb-gst-pct" />
          </div>
          <div>
            <Label>GST amount</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-slate-400 font-mono-fin pointer-events-none">₹</span>
              <Input type="number" value={form.gst_amount} readOnly
                className="h-10 bg-slate-50 font-mono-fin text-right text-[14px] pl-6 pr-3 tabular-nums" />
            </div>
          </div>
          <div>
            <Label>Total</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-red-600 font-mono-fin font-semibold pointer-events-none">₹</span>
              <Input type="number" value={form.total} readOnly
                className="h-10 bg-red-50 border-red-200 font-mono-fin text-right font-semibold text-[15px] pl-6 pr-3 tabular-nums text-red-900"
                data-testid="nb-total" />
            </div>
          </div>
        </div>

        {form.total && Number(form.total) > 0 && (
          <div className="p-3 bg-red-50 border border-red-100 text-sm text-red-900">
            {isBill ? "Amount owed" : "Amount receivable"}: <span className="font-mono-fin font-semibold">{formatRupees(Number(form.total))}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={() => nav(backTo)} className="rounded-md h-10">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-md h-10 gap-2 bg-red-600 hover:bg-red-700 text-white" data-testid="nb-save">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : `Save ${isBill ? "bill" : "invoice"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
